export namespace AI {
    /** AI场景 */
    export enum AIScene {
        /** 流程审批智能分析 */
        FORM_APPROVE_ANALYSIS,

        /** 表单控件赋值 */
        FORM_CONTROL_VALUE_ASSIGN = 1,
    }

    /** AI指令名称枚举 */
    export enum Commands {
        /** 值表单控件赋 */
        FORM_CONTROL_VALUE_ASSIGN,
    }

    /** AI智能填单配置 */
    export interface IAIAssignSetting {
        recognitionFields: string[];

        recognitionRule: string;

        exampleText: string;

        exampleFiles: Array<{
            fileId: string;
            fileName: string;
            contentType: string;
            contentLength: number;
            sortKey: number;
            thumbnailUrl: string;
        }>;

        disabled: boolean;
    }

    /** AI智能填单识别类型 */
    export enum AIRecognitionType {
        /** 文本 */
        Text = 0,

        /** 文件 */
        File = 1,
    }

    /** AI智能填单识别Prompt */
    export interface IAIAssignPrompt {
        /** 识别内容 */
        content: string;

        /** 识别类型 */
        contentType: AIRecognitionType;
    }

    /** AI智能填单识别字段类型 */
    export enum AIRecognitionFieldType {
        /** 文本 */
        Text = 1,
        /** 数字 */
        Number = 2,
        /** 日期 */
        Date = 3,
        /** 子表 */
        List = 4,
    }

    /** AI识别模板类型 */
    export enum AIRecognitionTemplateType {
        /** 全部模板 */
        All = 0,
        /** 系统模板 */
        System = 1,
        /** 自定义模板 */
        Custom = 2,
    }

    /** ai场景类型 */
    export enum AIFeatureType {
        /** AI填单助手 */
        Extraction = 1,
        /** AI语音录入功能 */
        Asr = 2,
        /** AI识别（OCR文字识别） */
        Ocr = 3,
    }

    /** 样本图片文件类型 */
    export type SampleImageFileType = {
        /** 文件ID */
        fileId: string;
        /** 文件名 */
        fileName: string;
        /** 文件类型 */
        contentType: string;
        /** 文件大小 */
        contentLength: number;
        /** 缩略图 */
        thumbnailUrl: string;
        /** sort */
        sortKey?: number;
        /** 源文件 */
        source?: File;
        /** 存储key */
        storageKey?: string;
        /** 进度 */
        percent?: number;
        /** 上传状态 */
        status?: string;
    };

    /** AI识别模板模型 */
    export type IAIRecognitionTemplateModel = {
        /** 模板编码 */
        code?: string;
        /** 模板名称 */
        name: string;
        /** 模板描述 */
        description: string;
        /** 样本图片 */
        sampleImage?: string;
        /** 样本文件 */
        sampleFile?: SampleImageFileType;
        /** 模板类型 */
        type: AIRecognitionTemplateType;
        /** 主表 */
        table: {
            /** 字段 */
            fields: ITableTemplateModel[];
        };
        /** 排序 */
        sortIndex: number;
    };

    export type ITableTemplateModel = {
        id?: string;
        /** 字段名称 */
        name: string;
        /** 字段描述 */
        description: string;
        /** 字段类型 */
        type: AIRecognitionFieldType;
        /** 子表 */
        subTable?: {
            fields: ITableTemplateModel[];
        };
    };

    export namespace IGenerationApp {
        /** ai生成应用进度状态枚举 */
        export enum GenerationStatus {
            /** 待开始 */
            PENDING = 'pending',
            /** 进行中 */
            RUNNING = 'running',
            /** 成功 */
            SUCCESS = 'success',
            /** 失败 */
            FAILED = 'failed',
        }

        export enum GenerationType {
            /** 应用 */
            APP = 'app',
            /** 模块，即表单分组 */
            MODULE = 'module',
            /** 模型，即表单 */
            MODEL = 'model',
            /** 氚云，即安装 */
            H3YUN = 'h3yun',
        }

        export type ProgressTreeNode = {
            /** 文本 */
            name: string;
            /** 状态 */
            status: GenerationStatus;
            /** 类型 */
            type: GenerationType;
            /** 子级 */
            children?: ProgressTreeNode[];
        };

        export type ProgressFlatItem = {
            /** 文本 */
            name: string;
            /** 状态 */
            status: GenerationStatus;
            /** 子级 */
            children?: ProgressTreeNode[];
        };

        export enum IRequirementSubmitTypeEnum {
            /** 初始提交 */
            INITIAL = 'initial',
            /** 补充信息 */
            SUPPLEMENT = 'supplement',
            /** 跳过 */
            SKIP = 'skip',
        }

        export enum IAIGenerationSpecificationStatus {
            COMPLETE = 'COMPLETE',
            INCOMPLETE = 'INCOMPLETE',
            INVALID = 'INVALID',
        }

        export type IAIGenerationSpecificationModel = {
            /** 状态 */
            status: IAIGenerationSpecificationStatus;

            /** 关键问题 */
            followupQuestions?: Array<string>;

            /** 需求规格说明 */
            requirementSpec?: string;

            /** 无效需求内容提示 */
            failureReason?: string;
        };

        export type IAIGenerationProgressModel = {
            /** 任务id */
            taskId: string;

            /** 应用编码 */
            appCode: string;

            /** 当前动态名称 */
            currentStep: string;

            /** 状态 */
            status: GenerationStatus;

            /** 动态步骤 */
            steps: ProgressTreeNode[];
        };
    }

    export namespace IAIGenerationCommon {
        /** 获取AI使用量类型枚举 */
        export enum getUsageType {
            /** 获取应用使用量 */
            APP_GENERATION = 'APP_GENERATION',
            /** 获取表单使用量 */
            FORM_INFERENCE = 'FORM_INFERENCE',
        }
    }

    export namespace IGenerationForm {
        export type IAIGenerationControl = {
            /** 控件编码 */
            code: string;
            /** 控件名称 */
            name: string;
            /** 控件类型 */
            type: string;
            /** 额外配置 */
            extendInfo?: Record<string, any>;
        };

        export enum IErrorCode {
            /** 用量耗尽，无次数 */
            USAGE_EXHAUSTED = 'functionUsage.exceeded',
            /** 表单推断无字段 */
            NO_FIELDS = 'form.inference.noFields',
        }
    }

    export namespace Request {
        export type IAIRecognitionTemplate = {
            /** 模板编码 */
            code?: string;
            /** 模板名称 */
            name: string;
            /** 模板描述 */
            description: string;
            /** 样本图片 */
            sampleImage?: string;
            /** 模板类型 */
            type: AIRecognitionTemplateType;
            /** 主表 */
            table: {
                /** 字段 */
                fields: ITableTemplateModel[];
            };
            /** 排序 */
            sortIndex: number;
        };
    }

    export namespace Response {
        export type IAIRecognitionTemplateDetail = {
            code: string;
            name: string;
            description: string;
            sortIndex: number;
            type: AIRecognitionTemplateType;
            icon: string;
        };

        export type IAIRecognitionTemplateDefinitions = IAIRecognitionTemplateModel & {
            sampleImage: SampleImageFileType;
        };

        export type IAIRecognitionOcrExtraction = {
            name: string;
            value: string;
            children?: IAIRecognitionOcrExtraction[][];
        };

        /** 使用日志模型 */
        export type IFeatureUsageLogModel = {
            /** 任务id */
            objectId: string;
            /** 引擎编码 */
            engineCode: string;
            /** 表单编码 */
            schemaCode: string;
            /** 表单名称 */
            schemaName: string;
            /** 应用名称 */
            appName: string;
            /** 功能type */
            featureCode: AIFeatureType;
            /** 操作人ID */
            userId: string;
            /** 操作人名称 */
            userName: string;
            /** 操作时间 */
            logTime: string;
            /** 创建时间 */
            createdTime: string;
            /** 消耗次数 */
            creditUsed: number;
        };

        /** 使用日志模型(月统计) */
        export type IMonthlyFeatureUsageLogModel = {
            /** 月份 */
            statMonth: string;
            /** 调用次数 */
            totalCreditUsed: number;
        };

        export type IFeatureRecordModel = {
            /** 获得来源 */
            type: string;
            /** 公司名称 */
            companyName: string;
            /** 购买时间 */
            drpPaidTime: string;
            /** OCR识别次数 */
            ocrCount: number;
            /** 创建时间 */
            createdTime: string;
        };

        /** 启用清单模型 */
        export type IAIEnableModel = {
            /** 应用编码 */
            appCode: string;
            /** 应用名称 */
            appName: string;
            /** 表单编码 */
            schemaCode: string;
            /** 表单名称 */
            schemaName: string;
            /** 控件清单 */
            controlList: Array<{ controlCode: string; controlName: string }>;
        };

        export type IGetAIGenerationSpecification = IGenerationApp.IAIGenerationSpecificationModel;

        /** 开始AI生成 */
        export type IStartAIGeneration = {
            /** 生成任务id */
            taskId: string;
        };

        /** AI生成进度模型 */
        export type IGetAIGenerationProgress = IGenerationApp.IAIGenerationProgressModel;

        /** 企业当天可用ai生成应用次数 */
        export type IGetAIRemainingCredit = {
            /** 已使用量 */
            usedAmount: number;

            /** 使用上限 */
            usageLimit: number;
        };

        /** AI生成表单 */
        export type IGetAIGenerationForm = {
            /** 表单名称 */
            name: string;
            /** 字段列表 */
            fields: IGenerationForm.IAIGenerationControl[];
        };
    }
}
